<template>
    <div>
	<!-- DIALOGS -->  
        <SelectionRequiredDialog /> 
		<DeletionDialog context="SelectedPublishingProjects" />	
        <!-- Import: Import Releasefile -->
        <FileImportDialog :alvqaContext="alvqa +'-list'">
            <template v-slot:importOptions>
                <v-row v-if="isMappingImport">
                    <v-col>
                        <BaseEditPageDropdown
                            :context="context"
                            field="ImportSelectedMapping"
                            :label="$t('lang_GENERIC_CAPTION_ABBREVIATION')"
                            :items="mappingAbbreviations"
                            :clearable="false"
                            :withMargin="false"
                            :alvqa="alvqa + '-mapping-import-dropdown-select-abbreviation'"
                        />
                    </v-col>
                </v-row>
                <v-row v-if="isMappingImport" justify="center">
                    <v-col>
                        <BaseEditPageTextField
                            :context="context"
                            field="ImportTags"
                            :label="$t('lang_GENERAL_CAPTION_ADD_TAG')"
                            :withMargin="false"
                            :alvqa="alvqa + '-list-import-dialog-textfield-tags'"
                        />
                    </v-col>
                </v-row>
            </template>
        </FileImportDialog>
        <MoveIntoCompanyDialog 
            :context="'PublishingProjects'"
            :message="$t('lang_PUBLISHING_PROJECTS_CAPTION_MOVE_TEXT')"
        />
    
    <!-- LIST PAGE COMPONENT -->
        <BaseListPage
            headline="lang_PUBLISHING_PROJECTS"
            :context="context"
            :alvqa="alvqa"
            :stickyHeader="true"
        >   
        <template v-slot:filter>
        </template>
        <!-- DATA TABLE COMPONENT-->
            <template v-slot:dataTable>
                <BaseDataTableList
                    :headers="headers"
                    :context="context"
                    readOnlyGhostTdSpan="2"
                    :alvqa="alvqa"
                    :stickyTopSlot="true"
                >
                <!-- TOPSLOT: actions, imports etc. -->
                    <template v-slot:topSlot><PublishingProjectsTopSlot /></template>
                <!-- DATA TABLE CONTENT: columns -->
                    <template v-slot:body="{ item }">
						<ExpandIcon :isExpanded="item.isExpanded" />  
                        <!-- <td class="no_ellipsis">{{ item.item.image }}</td> -->
                        <td class="ellipsis">
                                <BaseTooltip 
                                    :simpleText="item.item.title" 
                                    :alvqa="alvqa +'-list-row-' + item.item.alvId + '-text-name'"
                                />
                        </td>
                        <td class="ellipsis">
                            <BaseTooltip 
                                :simpleText="item.item.composers.join(' / ')"
                                :alvqa="alvqa +'-list-row-' + item.item.alvId + '-text-composers'"
                            />
                        </td>
                        <td class="ellipsis">
                            <BaseTooltip 
                                :simpleText="item.item.publishers.join(' / ')" 
                                :alvqa="alvqa +'-list-row-' + item.item.alvId + '-text-publishers'"
                            />
                        </td>
                        <td class="no_ellipsis">
                            <div :alvqa="alvqa +'-list-row-' + item.item.alvId + '-text-alv-id'">
                                {{ item.item.alvId }}
                            </div>
                        </td>
                    </template>
                <!-- EXPAND: Read Only View -->
                    <template v-slot:expandSlot="{ id }"><PublishingProjectsReadOnly :itemId="id"/></template>
                </BaseDataTableList>
            </template>
        </BaseListPage>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseListPage from '@/components/_base/BaseListPage'
import BaseTooltip from '@/components/_base/BaseTooltip'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'

import PublishingProjectsTopSlot from './PublishingProjectsTopSlot'
import PublishingProjectsReadOnly from './_readonly/PublishingProjectsReadOnly'

import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import FileImportDialog from '@/components/_dialogs/FileImportDialog'
import MoveIntoCompanyDialog from '@/components/_dialogs//MoveIntoCompanyDialog'

export default {
    name: "PublishingProjectsList",
    components: {
        BaseDataTableList,
        BaseListPage,
        BaseTooltip,
        ExpandIcon,
        BaseEditPageTextField,
        BaseEditPageDropdown,

        PublishingProjectsTopSlot,
        PublishingProjectsReadOnly,

        FileImportDialog,
        SelectionRequiredDialog,
        DeletionDialog,
        MoveIntoCompanyDialog,
    },
    data() {
        return {
            context: "PublishingProjects",
            alvqa: "publishing-projects"
        }
    },
    computed: {
        ...mapGetters(['getPublishingProjectsSearch']),

        isMappingImport() {
            return this.$store.getters.getSelectedFileImport === 'mappingImport';
        },
        mappingAbbreviations() {
            let mappingsList = this.$store.getters.getMappings
            let retArr = []

            mappingsList.forEach((v) => {
                if (v.direction !== 'import') return;
                retArr.push({
                    value: v.alvId,
                    text: v.abbreviation,
                    alvqa: this.alvqa + `-import-mapping-${v.alvId}`,
                    disabled: v.columnMappingsCount === 0, // Disable if no column mappings exist for this mapping
                })
            })

            // Sort by ALVID descending (highest first), with disabled options at the end
            retArr.sort((a, b) => {
                if (a.disabled === true) return 1; // Put disabled at the end
                if (b.disabled === true) return -1;
                const alvIdA = parseInt(a.value.replace(/\D/g, '')) || 0
                const alvIdB = parseInt(b.value.replace(/\D/g, '')) || 0
                return alvIdB - alvIdA
            })

            // Auto-select first option and show warning if none (no watchers, contained here)
            if (this.isMappingImport && retArr.length > 0) {
                const selected = this.$store.getters.getPublishingProjectsDetailsImportSelectedMapping
                const found = retArr.find(o => o.value === selected)
                if ((selected === null || selected === undefined || !found) && retArr[0].disabled === false) {
                    this.$store.commit('setPublishingProjectsDetailsImportSelectedMapping', retArr[0].value)
                }
            }
            if (this.isMappingImport && retArr.length === 0 && this.$store.getters.getIsLoading === false) {
                const warn = this.$t('lang_GENERIC_MESSAGE_WARNING_NO_MAPPINGS_AVAILABLE_FOR_DOMAIN')
                this.$store.commit('setImportDialogText', `<span class="error--text">${warn}</span>`)
            }

            return retArr
        },

		headers() {
			return [
				{
					text: '', 
					value: 'data-table-select', 
					align: 'center',
                    sortable: false,
					width: "60px"
				},
				{
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    sortable: false,
					width: "48px"
				},
                // {
                //     text: '', 
                //     value: 'image', 
                //     align: 'center', 
                //     sortable: false, 
                //     class: 'text-no-wrap', 
                //     // width: "4%"
                // },
                {
                    text: this.$t('lang_PUBLISHING_PROJECTS_CAPTION_TITLE'), 
                    value: 'title', 
                    align: 'left', 
                    sortable: false,
                    width: '300px',
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_COMPOSERS'), 
                    value: 'composers', 
                    align: 'left', 
                    sortable: false,
                    width: '180px',
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_PUBLISHING_CAPTION_PUBLISHERS'), 
                    value: 'publishers', 
                    align: 'left', 
                    sortable: false,
                    class: 'text-no-wrap'
                },
				{
					text: this.$t('lang_CAPTION_ALVID'),
					value: 'alvId', 
					align: 'left', 
					sortable: 'true',
                    width: "170px",
                    sortable: false,
                    class: 'text-no-wrap'
				},
				{
					text: '', 
					value: 'ghost',
					sortable: false,
                    width: "1%",
                    class: 'text-no-wrap'
				},
				{
					text: '', 
					value: 'editForm',
					align: 'right',
					sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
				}	
			]
		}         
    },
    methods: {
        getMappings() {
            this.$store.commit('setSelectedMappingsDomainsFilter', 'publishing_projects')
            this.$store.dispatch('fetchMappingsByDomain')
        }
    },
    created() {
        this.getMappings()
    }
}
</script>